import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { CheckCircle2, BookOpen, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ResultsStepProps {
  leadScore: number;
  onBack: () => void;
}

const ResultsStep = ({ leadScore, onBack }: ResultsStepProps) => {
  const isQualified = leadScore >= 25;

  return (
    <div className="space-y-8">
      {isQualified ? (
        // Qualified Screen
        <>
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", duration: 0.6 }}
            className="flex justify-center"
          >
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-gold/30 to-gold-light/30 flex items-center justify-center backdrop-blur-sm border-2 border-gold/50 shadow-[0_0_40px_hsl(var(--gold)/0.35)]">
              <CheckCircle2 className="w-12 h-12 text-gold" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-center space-y-4"
          >
            <h2 className="text-4xl sm:text-5xl font-bold text-foreground">
              You're a Perfect Fit!
            </h2>
            <p className="text-xl text-foreground/80 max-w-xl mx-auto">
              Your automation potential is high. Let's build your custom strategy to scale your author business.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="p-8 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10"
          >
            <div className="text-center space-y-6">
              <div className="flex items-center justify-center gap-3">
                <Calendar className="w-6 h-6 text-gold" />
                <h3 className="text-2xl font-semibold text-foreground">
                  Book Your Strategy Call
                </h3>
              </div>
              
              <p className="text-muted-foreground">
                Schedule a 30-minute consultation where we'll:
              </p>
              
              <ul className="text-left space-y-3 max-w-md mx-auto">
                {[
                  "Analyze your specific automation needs",
                  "Design a custom AI workflow strategy",
                  "Show you proven case studies from authors",
                  "Discuss pricing and implementation timeline"
                ].map((item, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.6 + index * 0.1 }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle2 className="w-5 h-5 text-gold mt-0.5 flex-shrink-0" />
                    <span className="text-foreground/80">{item}</span>
                  </motion.li>
                ))}
              </ul>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1 }}
                className="pt-4"
              >
                <Button
                  onClick={() => window.open("https://meetings.series-a.co/meetings/series-a/authorpro", "_blank")}
                  size="lg"
                  className="bg-gradient-to-r from-gold via-gold-light to-gold hover:shadow-[0_0_30px_hsl(var(--gold)/0.45)] text-gold-foreground font-semibold px-8 py-6 text-lg transition-all duration-300 hover:scale-105 w-full sm:w-auto"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Book Strategy Call
                </Button>
              </motion.div>
            </div>
          </motion.div>
        </>
      ) : (
        // Nurture Screen
        <>
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", duration: 0.6 }}
            className="flex justify-center"
          >
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-gold/20 to-gold-light/20 flex items-center justify-center backdrop-blur-sm border-2 border-gold/40 shadow-[0_0_40px_hsl(var(--gold)/0.35)]">
              <BookOpen className="w-12 h-12 text-gold" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-center space-y-4"
          >
            <h2 className="text-4xl sm:text-5xl font-bold text-foreground">
              You're on the Right Track
            </h2>
            <p className="text-xl text-foreground/80 max-w-xl mx-auto">
              You're not quite ready for a custom build yet — the Write platform is the best place to start.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="p-8 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10"
          >
            <div className="text-center space-y-6">
              <h3 className="text-2xl font-semibold text-foreground">
                Start Your Book on the Write Platform
              </h3>
              
              <p className="text-muted-foreground">
                Go from idea to finished chapters with guided, interactive steps:
              </p>
              
              <ul className="text-left space-y-3 max-w-md mx-auto">
                {[
                  "Shape your idea into a clear book concept",
                  "Build a chapter-by-chapter outline",
                  "Draft chapters with AI guidance in your voice",
                  "Review your manuscript when you are ready"
                ].map((item, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.6 + index * 0.1 }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle2 className="w-5 h-5 text-gold mt-0.5 flex-shrink-0" />
                    <span className="text-foreground/80">{item}</span>
                  </motion.li>
                ))}
              </ul>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1 }}
                className="space-y-3 pt-4"
              >
                <Button
                  asChild
                  size="lg"
                  className="bg-gradient-to-r from-gold via-gold-light to-gold hover:shadow-[0_0_30px_hsl(var(--gold)/0.45)] text-gold-foreground font-semibold px-8 py-6 text-lg transition-all duration-300 hover:scale-105 w-full sm:w-auto"
                >
                  <Link to="/sign-in">
                    <BookOpen className="w-5 h-5 mr-2" />
                    Start Your Book Today
                  </Link>
                </Button>

                <p className="text-sm text-muted-foreground">
                  Or{" "}
                  <button
                    onClick={() => window.open("https://meetings.series-a.co/meetings/series-a/authorpro", "_blank")}
                    className="text-gold hover:underline"
                  >
                    book a call anyway
                  </button>
                </p>
              </motion.div>
            </div>
          </motion.div>
        </>
      )}

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="text-center"
      >
        <p className="text-sm text-muted-foreground">
          Your score: <span className="text-gold font-semibold">{leadScore}</span> out of 30 possible points
        </p>
      </motion.div>
    </div>
  );
};

export default ResultsStep;
